import React, { Fragment, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { Box, TableBody } from '@mui/material';
import {
  getEditableMode,
  getGroupBy,
  getAccountListFetchErrorMessage,
  GroupAccountMembersBy,
  resetDummyProjects,
  setAccountDummyMembers,
} from '../../redux/account';
import { ProjectRow } from './components';
import { TeamMemberView } from './TeamMemberView';
import { CompensationDummyMemberRow } from './CompensationDummyMemberRow';
import { StyledTableRow } from './StyledTableRow';
import { TableCell, Typography } from '@mui/material';
import {
  Account,
  DummmyProjectDetail,
  DummyMember,
  GroupedMembers,
  MarginHighlight,
} from '../../types';
import { CustomTableCell } from '../../components/customTable';

interface TeamBudgetsTableViewProps
  extends Pick<Account, 'grossMarginHighlights' | 'netMarginHighlights'> {
  groupedMembers: GroupedMembers[];
  dummyMembers?: DummyMember[] | null;
  dummyProjects?: DummmyProjectDetail[] | null;
  hasAccounts: boolean;
  isEditableMode?: boolean | false;
  isProjectView: boolean;
  accountName?: string;
}

export const TeamListMemberView = ({
  groupedMembers,
  hasAccounts,
  grossMarginHighlights,
  netMarginHighlights,
  dummyMembers,
  dummyProjects,
  isProjectView,
  isEditableMode = false,
  accountName,
}: TeamBudgetsTableViewProps) => {
  const containerRef = useRef(null);
  const dispatch = useAppDispatch();
  const editableMode = useAppSelector(getEditableMode);
  const groupBy = useAppSelector(getGroupBy);
  const accountListFetchError = useAppSelector(getAccountListFetchErrorMessage);

  useEffect(() => {
    if (!editableMode) {
      dispatch(setAccountDummyMembers());
      dispatch(resetDummyProjects());
    }
  }, [editableMode, dispatch]);

  if (!groupedMembers?.length && accountListFetchError)
    return (
      <CustomTableCell colSpan={13} pure>
        <Box
          sx={{
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            marginTop: '3%',
            marginBottom: '3%',
            fontSize: '1rem',
            color: 'grey',
          }}
        >
          {accountListFetchError}
        </Box>
      </CustomTableCell>
    );

  return (
    <TableBody
      ref={containerRef}
      sx={{
        'tr:not(:last-child, .projectRow)': {
          boxShadow: 'inset 0px -1px 0px rgba(0, 0, 0, 0.12)',
        },
        '.projectRow': {
          position: 'sticky',
          top: '56px',
          zIndex: 2,
        },
      }}
    >
      {!!dummyProjects?.length &&
        dummyProjects.map((dummyProject, index) => (
          <Fragment key={index}>
            {!isProjectView && (
              <ProjectRow
                projectId={dummyProject.id}
                key={dummyProject.id}
                name={dummyProject.name}
                members={`${dummyProject.membersCount ?? 0} members`}
                grossMargin={dummyProject.grossMargin}
                netMargin={dummyProject.netMargin}
                grossMarginHighlights={dummyProject.grossMarginHighlights ?? []}
                netMarginHighlights={dummyProject.netMarginHighlights ?? []}
                //Check if grouping is set on team name, can't get margin for other groupings as FSS can't calculate it
                showMargin={true}
                hideLink
              />
            )}
            {hasAccounts && dummyMembers && (
              <DummyMembers
                dummyMembers={dummyMembers}
                projectId={dummyProject.id}
                grossMarginHighlights={grossMarginHighlights ?? []}
                netMarginHighlights={netMarginHighlights ?? []}
                containerRef={containerRef}
              />
            )}
          </Fragment>
        ))}
      {groupedMembers?.map(
        ({
          groupName,
          members,
          projectId,
          grossMargin,
          netMargin,
          grossMarginHighlights,
          netMarginHighlights,
        }) => (
          <Fragment key={groupName}>
            {!isProjectView && (
              <ProjectRow
                projectId={projectId}
                key={groupName}
                name={groupName}
                members={`${members?.length ?? 0} members`}
                grossMargin={grossMargin}
                netMargin={netMargin}
                grossMarginHighlights={grossMarginHighlights ?? []}
                netMarginHighlights={netMarginHighlights ?? []}
                //Check if grouping is set on team name, can't get margin for other groupings as FSS can't calculate it
                showMargin={groupBy === GroupAccountMembersBy.Team}
              />
            )}
            {hasAccounts && projectId && dummyMembers && (
              <DummyMembers
                dummyMembers={dummyMembers}
                projectId={projectId}
                grossMarginHighlights={grossMarginHighlights ?? []}
                netMarginHighlights={netMarginHighlights ?? []}
                containerRef={containerRef}
              />
            )}
            {hasAccounts && members.length ? (
              <>
                {members.map((member, index) => (
                  <TeamMemberView
                    id={member.id}
                    key={member.id}
                    fullName={member.fullName}
                    seniority={member.seniority}
                    stream={member.stream}
                    compensationRate={member.compensationRate}
                    resource={{ billRate: member.resource?.billRate || 0 }}
                    companyRateCard={member.companyRateCard}
                    allocation={member.allocation}
                    grossMargin={member.grossMargin}
                    netMargin={member.netMargin}
                    grossMarginHighlights={grossMarginHighlights || []}
                    netMarginHighlights={netMarginHighlights || []}
                    isLastRow={members.length - 1 === index}
                    isEditableMode={isEditableMode}
                    isRestricted={false}
                    isHidden={member.editable?.isHidden}
                    compensationRange={member.compensationRange}
                    avatarUrl={member.avatarUrl}
                    actualPayRate={member.actualPayRate}
                    compensationType={member.compensationType}
                    legalLocation={member.legalLocation}
                    projectId={projectId}
                    accountName={accountName}
                  />
                ))}
              </>
            ) : (
              <StyledTableRow isLastRow={true} isHidden={true}>
                <TableCell align="left">
                  <Typography
                    whiteSpace="nowrap"
                    textAlign="center"
                    variant="h4"
                    color="slategray"
                  >
                    There are no members in this team
                  </Typography>
                </TableCell>
              </StyledTableRow>
            )}
          </Fragment>
        ),
      )}
    </TableBody>
  );
};

const DummyMembers = ({
  dummyMembers,
  projectId,
  grossMarginHighlights,
  netMarginHighlights,
  containerRef,
}: {
  dummyMembers: DummyMember[];
  projectId: string;
  grossMarginHighlights: MarginHighlight[];
  netMarginHighlights: MarginHighlight[];
  containerRef: React.RefObject<Element>;
}) => {
  return (
    <>
      {dummyMembers
        .filter(
          (dummyMember: DummyMember) => dummyMember.projectId === projectId,
        )
        .map((dummyMember, index) => (
          <CompensationDummyMemberRow
            id={dummyMember.id}
            current={containerRef.current}
            key={dummyMember.id}
            isLastRow={dummyMembers.length - 1 === index}
            dummyMember={dummyMember}
            grossMarginHighlights={grossMarginHighlights || []}
            netMarginHighlights={netMarginHighlights || []}
            projectId={projectId}
          />
        ))}
    </>
  );
};
